import { useTranslation } from 'react-i18next';
import { MAX_NAME_LENGTH } from '../../constants';
import { AlarmScheduleFields } from './AlarmScheduleFields';
import { TextField } from '../../../modal/fields/TextField';
import { TimeInput } from './TimeInput';
import type { useAlarmScheduleField } from '../hooks/useAlarmScheduleField';
import type { ModalFieldChangeEvent } from '../hooks/useModalForm';
import type { ModalFormData } from '../types';

interface LdenAlarmFormProps {
    formData: ModalFormData;
    errors: Record<string, string>;
    handleChange: (event: ModalFieldChangeEvent) => void;
    handleDayChange: (selectedDay: number, isStartTime: boolean) => void;
    alarmSettings: ReturnType<typeof useAlarmScheduleField>['settings'];
}

export function LdenAlarmForm({
    formData,
    errors,
    handleChange,
    handleDayChange,
    alarmSettings,
}: LdenAlarmFormProps) {
    const { t } = useTranslation();

    return (
        <div className="max-h-[70vh] flex-1 overflow-y-auto overflow-x-hidden px-6">
            <TextField
                label={gettext('ALARM_NAME')}
                name="name"
                value={formData.name}
                onChange={handleChange}
                maxLength={MAX_NAME_LENGTH}
                error={errors.name}
            />
            <TimeInput
                label={gettext('START_TIME')}
                time={formData.startTime}
                onChange={handleChange}
                name="startTime"
                error={errors.startTime}
                day={formData.startDay}
                onDayChange={(day) => handleDayChange(day, true)}
                dayError={errors.startDay}
            />
            <TimeInput
                label={gettext('END_TIME')}
                time={formData.endTime}
                onChange={handleChange}
                name="endTime"
                error={errors.endTime}
                day={formData.endDay}
                onDayChange={(day) => handleDayChange(day, false)}
                dayError={errors.endDay}
            />

            <div className="rounded-md border-2 border-solid p-2">
                <TextField
                    label={gettext('CORRECTION')}
                    type="number"
                    step={0.1}
                    name="correction"
                    value={formData.correction}
                    onChange={handleChange}
                    error={errors.correction}
                />
                <TextField
                    label={gettext('WARNING_LEVEL')}
                    type="number"
                    step={0.1}
                    name="warningLevel"
                    value={formData.warningLevel}
                    onChange={handleChange}
                    error={errors.warningLevel}
                    helperText={t('SOUND_MEASURING_POINT__FIELD__traceSaveLevel__HELP_TEXT')}
                />
                <TextField
                    label={gettext('ALARM_LEVEL')}
                    type="number"
                    step={0.1}
                    name="alarmLevel"
                    value={formData.alarmLevel}
                    onChange={handleChange}
                    error={errors.alarmLevel}
                    helperText={t('SOUND_MEASURING_POINT__FIELD__traceSaveLevel__HELP_TEXT')}
                />
            </div>

            <AlarmScheduleFields
                formData={formData}
                handleChange={handleChange}
                errors={errors}
                settings={alarmSettings}
            />
        </div>
    );
}
